import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { formatDateLocal } from '../../utils/dateUtils';

interface User {
  id: number;
  username: string;
  email: string;
  role?: string;
}

interface AttendanceEntry {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  date: string;
  checkin: string | null;
  checkout: string | null;
  in_position: string | null;
  out_position: string | null;
  task_hours?: number;
  total_hours?: number;
  log_type?: string;
}

interface MonthViewProps {
  users: User[];
  attendance: AttendanceEntry[];
  loading: boolean;
  canManage?: boolean;
  onEdit?: (entry: AttendanceEntry) => void;
  onDelete?: (id: number, logType?: string) => void;
  onDateRangeChange?: (range: { dateFrom: string; dateTo: string }) => void;
}

interface MonthInfo {
  date: Date;
  label: string;
  year: number;
  month: number;
}

export default function MonthView({ 
  users, 
  attendance, 
  loading,
  onDateRangeChange,
}: MonthViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Sort users: by role (System Admin > Sale > Field Worker), then alphabetically
  const sortedUsers = useMemo(() => {
    const rolePriority: Record<string, number> = {
      'SYSTEM ADMIN': 0,
      'SALE': 1,
      'FIELD WORKER': 2,
    };
    return [...users].sort((a, b) => {
      const roleA = rolePriority[a.role?.toUpperCase() || ''] ?? 99;
      const roleB = rolePriority[b.role?.toUpperCase() || ''] ?? 99;
      if (roleA !== roleB) return roleA - roleB;
      return (a.username || a.email).localeCompare(b.username || b.email);
    });
  }, [users]);

  // Get 12 months (January - December of the selected year)
  const months = useMemo(() => {
    const monthsList: MonthInfo[] = [];
    const year = currentDate.getFullYear();

    for (let m = 0; m < 12; m++) {
      const monthDate = new Date(year, m, 1);
      monthDate.setHours(0, 0, 0, 0);

      monthsList.push({
        date: monthDate,
        label: monthDate.toLocaleDateString('en-US', { month: 'short' }),
        year: monthDate.getFullYear(),
        month: monthDate.getMonth(),
      });
    }
    return monthsList;
  }, [currentDate]);

  // Notify parent of date range change
  useEffect(() => {
    if (onDateRangeChange && months.length > 0) {
      const firstMonth = months[0];
      const lastMonth = months[months.length - 1];
      const dateFrom = formatDateLocal(new Date(firstMonth.year, firstMonth.month, 1));
      const lastDay = new Date(lastMonth.year, lastMonth.month + 1, 0);
      const dateTo = formatDateLocal(lastDay);
      onDateRangeChange({ dateFrom, dateTo });
    }
  }, [months, onDateRangeChange]);

  // Create a map of attendance by user_id and date
  const attendanceMap = useMemo(() => {
    const map = new Map<string, AttendanceEntry>();
    attendance.forEach((entry) => {
      if (entry.date && entry.user_id) {
        const key = `${entry.user_id}-${entry.date}`;
        map.set(key, entry);
      }
    });
    return map;
  }, [attendance]);

  // Calculate hours worked for a month
  const calculateMonthHours = (userId: number, monthInfo: MonthInfo): { totalHours: number; daysWorked: number; daysPresent: number } => {
    let totalHours = 0;
    let daysWorked = 0;
    let daysPresent = 0;

    // Get first and last day of the month
    const lastDay = new Date(monthInfo.year, monthInfo.month + 1, 0);

    // Iterate through all days in the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(monthInfo.year, monthInfo.month, day);
      const dateStr = formatDateLocal(date);
      const key = `${userId}-${dateStr}`;
      const entry = attendanceMap.get(key);

      if (entry) {
        if (entry.checkin) {
          daysPresent++;
        }
        if (entry.checkin && entry.checkout) {
          daysWorked++;
          // Use total_hours from backend if available (calculated from individual records)
          // Otherwise fall back to calculating from checkin/checkout
          if (entry.total_hours !== undefined && entry.total_hours !== null) {
            totalHours += entry.total_hours;
          } else {
            try {
              const checkinTime = new Date(entry.checkin);
              const checkoutTime = new Date(entry.checkout);
              const diffMs = checkoutTime.getTime() - checkinTime.getTime();
              const diffHours = diffMs / (1000 * 60 * 60);
              totalHours += diffHours;
            } catch {
              // Ignore invalid dates
            }
          }
        }
      }
    }

    return {
      totalHours: Math.round(totalHours * 100) / 100,
      daysWorked,
      daysPresent,
    };
  };

  // Get month summary chip
  const getMonthChip = (hours: number, daysWorked: number, daysPresent: number) => {
    if (daysWorked === 0 && daysPresent === 0) {
      return <Chip label="-" color="default" size="small" />;
    }
    const label = `${hours.toFixed(0)}h / ${daysWorked}d`;
    return (
      <Chip 
        label={label} 
        color={daysWorked >= 20 ? 'success' : daysWorked >= 10 ? 'warning' : 'default'} 
        size="small" 
      />
    );
  };

  const handlePreviousYear = () => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(newDate.getFullYear() - 1);
    setCurrentDate(newDate);
  };

  const handleNextYear = () => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(newDate.getFullYear() + 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Month Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton onClick={handlePreviousYear}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" sx={{ cursor: 'pointer' }} onClick={handleToday}>
          {currentDate.toLocaleDateString('en-US', { year: 'numeric' })}
        </Typography>
        <IconButton onClick={handleNextYear}>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: { xs: 100, sm: 200 }, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'background.paper' }}>
                User
              </TableCell>
              {months.map((month) => (
                <TableCell key={month.label} align="center" sx={{ minWidth: 120 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {month.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => {
              const monthStats = months.map((month) => calculateMonthHours(user.id, month));
              const totalHours = monthStats.reduce((sum, stat) => sum + stat.totalHours, 0);
              const totalDays = monthStats.reduce((sum, stat) => sum + stat.daysWorked, 0);

              return (
                <TableRow key={user.id} hover>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      backgroundColor: 'background.paper',
                      fontWeight: 'medium',
                      maxWidth: { xs: 100, sm: 200 },
                      width: { xs: 100, sm: 200 },
                    }}
                  >
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {user.username || user.email.split('@')[0]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {user.email}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Total: {totalHours.toFixed(0)}h / {totalDays}d
                      </Typography>
                    </Box>
                  </TableCell>
                  {months.map((month, idx) => {
                    const stats = monthStats[idx];
                    return (
                      <TableCell key={month.label} align="center">
                        <Tooltip
                          title={`Days worked: ${stats.daysWorked}\nDays present: ${stats.daysPresent}\nTotal hours: ${stats.totalHours.toFixed(1)}h`}
                        >
                          {getMonthChip(stats.totalHours, stats.daysWorked, stats.daysPresent)}
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
